import type { AssessmentPortalConfig, FlowStepDefinition } from '../../assessment-portal';
import type { AdhdQuestionnaireFlowState } from '../types/flow';
import {
  QUESTIONNAIRE_BASE,
  ASSESSMENT_TYPE,
  QUESTIONNAIRE_TEMPLATE_ID,
  AdhdQuestionnaireFlowStep,
} from '../data/constants';
import {
  AgeRangeStep,
  AssessmentIntroStep,
  AssessmentQuestionnaireStep,
  IdentityConfirmStep,
  QuestionnaireCompletionStep,
} from '../pages/steps';
import { YOUNG_QUESTIONNAIRE_ENABLED } from './feature-flags';

const createAdhdQuestionnaireInitialFlowState = (
  youngQuestionnaireEnabled: boolean,
): AdhdQuestionnaireFlowState => ({
  // With the young questionnaire off there is no age range page: everyone gets the adult one.
  ageRange: youngQuestionnaireEnabled ? null : 'adult',
  questionnaireAnswers: {},
  questionnaireMeta: {
    templateId: QUESTIONNAIRE_TEMPLATE_ID,
    templateVersion: 1,
  },
  identityConfirm: {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
  },
  submissionResult: {
    isSuccess: false,
    errorMessage: '',
  },
  completionDisplay: null,
});

// Young DIVA-5 page views carry this suffix so reports can tell the versions apart.
const YOUNG_ANALYTICS_LABEL_SUFFIX = ' (Young)';

/** Steps after the age range page are reported per questionnaire version. */
const resolveQuestionnaireAnalyticsLabel = (
  step: FlowStepDefinition,
  flowState: AdhdQuestionnaireFlowState,
): string => (
  step.key !== AdhdQuestionnaireFlowStep.AGE_RANGE && flowState.ageRange === 'young'
    ? `${step.label}${YOUNG_ANALYTICS_LABEL_SUFFIX}`
    : step.label
);

const AGE_RANGE_STEP: FlowStepDefinition = {
  key: 'ageRange',
  path: '/age',
  label: 'Age Range',
  component: AgeRangeStep,
  layout: 'full',
};

// Paths match the released flow when the young questionnaire is off.
const buildFlowSteps = (youngQuestionnaireEnabled: boolean): FlowStepDefinition[] => [
  ...(youngQuestionnaireEnabled ? [AGE_RANGE_STEP] : []),
  {
    key: 'assessmentIntro',
    path: youngQuestionnaireEnabled ? '/intro' : '',
    label: 'Assessment Intro',
    component: AssessmentIntroStep,
    layout: 'full',
  },
  {
    key: 'questionnaireAnswers',
    path: '/questions',
    label: 'Assessment Questionnaire',
    component: AssessmentQuestionnaireStep,
    layout: 'full',
  },
  {
    key: 'identityConfirm',
    path: '/submit',
    label: 'Confirm Identity',
    component: IdentityConfirmStep,
    layout: 'full',
  },
  {
    key: 'completion',
    path: '/done',
    label: 'Completion',
    component: QuestionnaireCompletionStep,
    layout: 'full',
  },
];

/**
 * Young questionnaire on: every visit starts on the age range page.
 * Off: the released flow — the intro is the base path and a reload returns to it.
 */
const buildStepRouting = (
  youngQuestionnaireEnabled: boolean,
): Pick<AssessmentPortalConfig<AdhdQuestionnaireFlowState>, 'reloadReset' | 'resolveInitialStepIndex'> => (
  youngQuestionnaireEnabled
    // Flow state starts empty on every entry, so /adhd/questionnaire and deep links
    // redirect to the age range page (the first step).
    ? { resolveInitialStepIndex: () => 0 }
    : { reloadReset: { toStepKey: AdhdQuestionnaireFlowStep.ASSESSMENT_INTRO } }
);

export const buildAdhdQuestionnaireConfig = (
  youngQuestionnaireEnabled: boolean,
): AssessmentPortalConfig<AdhdQuestionnaireFlowState> => ({
  specialtyKey: 'adhd-questionnaire',
  assessmentType: ASSESSMENT_TYPE,
  specialtyId: process.env.REACT_APP_ADHD_SPECIALTY_ID as string,
  questionnaireTemplateId: QUESTIONNAIRE_TEMPLATE_ID,

  basePath: QUESTIONNAIRE_BASE,
  flowSteps: buildFlowSteps(youngQuestionnaireEnabled),

  consentItems: [],
  consentCategoryMap: {},

  buildSubmissionPayload: (flowState) => flowState,

  createInitialFlowState: () => createAdhdQuestionnaireInitialFlowState(youngQuestionnaireEnabled),

  unloadWarningUntilReachedStepKey: AdhdQuestionnaireFlowStep.COMPLETION,

  ...buildStepRouting(youngQuestionnaireEnabled),

  resolveStepAnalyticsLabel: resolveQuestionnaireAnalyticsLabel,

  theme: {
    cssClass: 'adhd-portal',
  },
});

export const adhdQuestionnaireConfig = buildAdhdQuestionnaireConfig(YOUNG_QUESTIONNAIRE_ENABLED);
